<template>
  <div class="distributor-min-sum-page pa-6">
    <h2 class="mb-6">Управление минимальными суммами заказов</h2>
    
    <div class="mb-4">
      <v-btn 
        color="success"
        @click="toggleAddForm"
        :disabled="isLoading"
      >
        <v-icon left>{{ showAddForm ? 'mdi-close' : 'mdi-plus' }}</v-icon>
        {{ showAddForm ? 'Отменить' : 'Добавить новую запись' }}
      </v-btn>
    </div>

    <v-expand-transition>
      <v-card v-if="showAddForm" class="mb-6">
        <v-card-title>Добавление новой записи</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selectedFilial"
                :items="filials"
                item-text="name"
                item-value="id"
                label="Выберите филиал"
                :loading="isLoadingFilials"
                :search-input.sync="searchQuery"
                no-data-text="Филиалы не найдены"
                return-object
                clearable
                outlined
                dense
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>ID: {{ item.id }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="minSum"
                label="Минимальная сумма заказа"
                type="number"
                step="0.01"
                min="0"
                outlined
                dense
                @keyup.enter="createMinSum"
                suffix="₸"
              ></v-text-field>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12">
              <v-btn 
                color="primary"
                block
                @click="createMinSum"
                :disabled="!canCreate || isCreating"
                :loading="isCreating"
              >
                <v-icon left>mdi-check</v-icon>
                Добавить
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Существующие записи</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="loadData"
          :loading="isLoading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-data-table
        :headers="headers"
        :items="minSums"
        :loading="isLoading"
        loading-text="Загрузка..."
        no-data-text="Нет записей о минимальных суммах"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [10, 25, 50],
          'items-per-page-text': 'Записей на странице'
        }"
      >
        <template v-slot:item.filialName="{ item }">
          {{ getFilialName(item.filialId) }}
        </template>
        
        <!-- Минимальная сумма -->
        <template v-slot:item.minSum="{ item }">
          <template v-if="editingId === item.filialId">
            <v-text-field
              v-model.number="editSum"
              type="number"
              step="0.01"
              min="0"
              dense
              outlined
              hide-details
              @keyup.enter="saveEdit(item.filialId)"
              @keyup.escape="cancelEdit"
              suffix="₸"
            ></v-text-field>
          </template>
          <template v-else>
            <span class="font-weight-bold">{{ formatCurrency(item.minSum) }}</span>
          </template>
        </template>
        
        <template v-slot:item.actions="{ item }">
          <template v-if="editingId === item.filialId">
            <v-btn
              color="success"
              x-small
              @click="saveEdit(item.filialId)"
              :loading="isUpdating"
              class="mr-1"
            >
              <v-icon small left>mdi-check</v-icon>
              Сохранить
            </v-btn>
            <v-btn
              color="grey"
              x-small
              @click="cancelEdit"
            >
              <v-icon small left>mdi-close</v-icon>
              Отмена
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              color="info"
              x-small
              icon
              @click="startEdit(item)"
              :disabled="editingId !== null"
              class="mr-1"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              color="error"
              x-small
              icon
              @click="deleteMinSum(item.filialId)"
              :loading="isDeleting === item.filialId"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showDeleteConfirm" max-width="450">
      <v-card>
        <v-card-title class="headline">Подтверждение удаления</v-card-title>
        <v-card-text>
          Вы действительно хотите удалить минимальную сумму для филиала 
          <strong>"{{ getFilialName(deleteTargetId) }}"</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelDelete">Отмена</v-btn>
          <v-btn color="error" @click="confirmDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'DistributorMinSum',
  
  data() {
    return {
      showAddForm: false,
      searchQuery: '',
      selectedFilial: null,
      minSum: null,
      isCreating: false,
      
      editingId: null,
      editSum: null,
      isUpdating: false,
      isDeleting: null,
      
      showDeleteConfirm: false,
      deleteTargetId: null,
      
      headers: [
        { text: 'ID филиала', value: 'filialId', width: '100px' },
        { text: 'Название филиала', value: 'filialName', sortable: false },
        { text: 'Мин. сумма', value: 'minSum', width: '200px' },
        { text: 'Действия', value: 'actions', sortable: false, width: '150px', align: 'center' }
      ]
    }
  },
  
  computed: {
    ...mapState('distributorMinSum', ['minSums', 'filials', 'isLoading', 'isLoadingFilials', 'error']),
    ...mapGetters('distributorMinSum', ['getFilialName']),
    
    canCreate() {
      return this.selectedFilial && 
             this.minSum && 
             this.minSum > 0 && 
             !this.isCreating
    }
  },
  
  mounted() {
    this.loadData()
  },
  
  methods: {
    ...mapActions('distributorMinSum', [
      'fetchAllDistributorMinSums',
      'fetchAllFilials',
      'createDistributorMinSum',
      'updateDistributorMinSum',
      'deleteDistributorMinSum',
      'clearError'
    ]),
    
    async loadData() {
      await Promise.all([
        this.fetchAllDistributorMinSums(),
        this.fetchAllFilials()
      ])
    },
    
    toggleAddForm() {
      this.showAddForm = !this.showAddForm
      if (!this.showAddForm) {
        this.resetAddForm()
      }
    },
    
    resetAddForm() {
      this.searchQuery = ''
      this.selectedFilial = null
      this.minSum = null
      this.clearError()
    },
    
    async createMinSum() {
      if (!this.canCreate) return
      
      this.isCreating = true
      this.clearError()
      
      try {
        await this.createDistributorMinSum({
          filialId: this.selectedFilial.id,
          minSum: this.minSum
        })
        
        this.resetAddForm()
        this.showAddForm = false
        await this.fetchAllDistributorMinSums()
      } catch (err) {
        window.console.log(err)
      } finally {
        this.isCreating = false
      }
    },
    
    startEdit(item) {
      this.editingId = item.filialId
      this.editSum = item.minSum
    },
    
    cancelEdit() {
      this.editingId = null
      this.editSum = null
    },
    
    async saveEdit(filialId) {
      if (!this.editSum || this.editSum <= 0) {
        const Snackbar = {
          text: 'Сумма должна быть больше 0',
          timeout: 7500,
          color: "error",
          linkText: "",
          show: true,
        }
        this.$store.commit("SET_SNACKBAR", Snackbar, { root: true })
        return
      }
      
      this.isUpdating = true
      this.clearError()
      
      try {
        await this.updateDistributorMinSum({
          filialId: filialId,
          minSum: this.editSum
        })
        
        this.cancelEdit()
        await this.fetchAllDistributorMinSums()
      } catch (err) {
        window.console.log(err)
      } finally {
        this.isUpdating = false
      }
    },
    
    deleteMinSum(filialId) {
      this.deleteTargetId = filialId
      this.showDeleteConfirm = true
    },
    
    cancelDelete() {
      this.showDeleteConfirm = false
      this.deleteTargetId = null
    },
    
    async confirmDelete() {
      const filialId = this.deleteTargetId
      this.showDeleteConfirm = false
      this.deleteTargetId = null
      
      this.isDeleting = filialId
      this.clearError()
      
      try {
        await this.deleteDistributorMinSum(filialId)
        await this.fetchAllDistributorMinSums()
      } catch (err) {
        window.console.log(err)
      } finally {
        this.isDeleting = null
      }
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'KZT',
        minimumFractionDigits: 2
      }).format(value)
    }
  }
}
</script>

<style scoped>
.distributor-min-sum-page {
  max-width: 1200px;
  margin: 0 auto;
}
</style>